import React, { memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Types, Box } from '@mafc/trolly';
import { ArrowLeft, RightArrow } from '@mafc/icon';
import COLORS from 'theme/colors';
import CONSTANTS from 'config/constants';
import { NEW_CART_PAGE } from 'config/Urls.config';
import useCartShipments from 'hooks/useCartShipments.hook';
import useGTMVirtualPageTrakingEffect from './hooks/useGTMVirtualPageTrakingEffect';
import useReferrerUri from 'pages/Checkout/hooks/useReferrerUri.hook';
import useMergeCarts from './hooks/useMergeCarts.hook';
import PageMaxWidth from 'common/PageLayout/PageMaxWidth';
import CartCheckoutContainer from 'common/CartCheckoutContainer';
import { userSetAddressChange } from 'common/User/redux/User.actions';
import CartLayout from './components/CartLayout';

import { useBreakpoints, useMount } from '@mafc/hooks';
import Tracker from 'utils/Tracker';
import { isRTL } from 'config/localisation';
import { useMemo } from 'react';
import FEATURES_CONFIG from 'config/features';
import { hideNotification, showNotification } from 'common/Notification/redux/notification.action';
import { useDispatch, useSelector } from 'react-redux';
import { hidePageLoader } from 'common/PageLoader/redux/pageLoader.action';
import DynamicLoader from 'routes/DynamicLoader';
// import useShipments from 'services/shipments/hooks/useShipments.hook';
import CartLoader from './components/CartLoader';
import { i18n, useRelevantCarousel, IAppState, useAnalytic } from '@mafc/common';
import { fetchCart, getEarnedPoints, updateCart } from './redux/actions';
import { transfromGetCart } from './redux/actions/newCart.actions';
import { trackCartUpdation } from './components/UpdateQuantity/redux/updateQuantity.action';
import CartList from './components/CartList';
import styled from '@emotion/styled';
import { notificationTypes } from 'common/Notification/redux/notification.type';
import { initStorageManager } from 'utils/customStorage.util';
import { COUNTRY_CODE_KEY } from './constants/cart.constants';
import { BrazeUserAttributes } from 'pages/OrderConfirmation/utils/brazeContants';
import PaymentMethod from 'common/PaymentMethods';

const storage = initStorageManager(() => sessionStorage);

const EmptyCartView = DynamicLoader(() => import('./components/EmptyCartView')) as any;
const CartOrderSummary = DynamicLoader(() =>
  import(
    /* webpackChunkName: "cartOrderSummaryChunk" */
    './components/CartOrderSummary/index'
  )
) as any;
const isRelevanceCarouselEnabled = FEATURES_CONFIG.settings.enableRelevanceWidgetOnCart;

// TODO: reevaluate this implementation later on
const breadcrumbGenerator = (details: Types.BreadcrumbItemHolderInterface) => {
  const { label, to, isItemInTheEnd, redirect = false } = details;
  if (redirect) {
    return <a href={to}>{label}</a>;
  }
  return !isItemInTheEnd ? <Link to={to}>{label}</Link> : <p>{label}</p>;
};

const breadcrumbConfig: any = (t: any) => {
  let Icon = RightArrow;
  let testid = 'RightArrow';

  if (isRTL()) {
    Icon = ArrowLeft;
    testid = 'ArrowLeft';
  }

  return {
    routes: [
      { label: t('home'), to: window.location.origin, redirect: true },
      { label: t('cart'), to: '/app/cart' },
    ],
    renderItem: breadcrumbGenerator,
    separator: <Icon testid={testid} size="XS" fill={COLORS.THUNDORA} />,
  };
};

const PLACEMENTS = 'cart_page.web_rank1|cart_page.web_rank2|cart_page.web_rank3';
const placementArray = PLACEMENTS.split('|');

export const RRWrapper = styled(Box)`
  .prev-button svg {
    ${(props: any) => (props.isRTL ? 'right: 1px' : '')};
  }
  .next-button svg {
    ${(props: any) => (props.isRTL ? 'right: -1px' : '')};
  }
`;

const Cart: React.FC = () => {
  useReferrerUri(NEW_CART_PAGE);
  useGTMVirtualPageTrakingEffect('cart');
  const { isMobile } = useBreakpoints();
  const {
    cartState: { errorMessage, loadingEntries },
    isCartAPICompleted,
    isLoading,
    isCartEmpty,
    shipmentsState,
  } = useCartShipments();
  const { allShipments } = shipmentsState;

  const dispatch = useDispatch();
  const hidePageLoaderAction = useCallback(() => {
    dispatch(hidePageLoader());
  }, [dispatch]);
  // multi-lingual support
  const { t } = useTranslation();

  const { code: countryCode, storeId, language } = useSelector(
    (state: IAppState) => state.appConfig
  );
  storage.setItem(COUNTRY_CODE_KEY, countryCode);

  const productIDs = useMemo(() => {
    const shipmentIDList: string[] = [];
    if (!allShipments) {
      return '';
    }

    allShipments.forEach(({ entries }) => {
      entries.forEach(({ product }) => {
        shipmentIDList.push(product.code);
      });
    });
    return shipmentIDList.join('|');
  }, [allShipments]);

  const { type } = useSelector(({ notification }: IAppState) => notification);
  const { BrazeSetCustomUserAttribute } = useAnalytic();
  // Merge Carts
  useMergeCarts(t);
  useMount(() => {
    Tracker.emptyDataLayer();
    Tracker.gtmPageLoad('cart_page');
  });
  useEffect(() => {
    if (type !== 'error') {
      hideNotification();
    }
  }, [type]);
  useEffect(hidePageLoaderAction, [hidePageLoaderAction]);
  useEffect(() => {
    window.document.title = t('header.cart');
  }, [t]);

  useEffect(() => {
    const brazeLastViewedPage = `${storeId}/${language}${window.location.pathname}`;
    BrazeSetCustomUserAttribute({ [BrazeUserAttributes.last_viewed_page]: brazeLastViewedPage });
  }, [BrazeSetCustomUserAttribute, language, storeId]);

  const { addressChanged, selectedAddress } = useSelector(({ userData }: IAppState) => userData);

  useEffect(() => {
    if (addressChanged) {
      dispatch(fetchCart());
      dispatch(userSetAddressChange(false));
    }
  }, [dispatch, addressChanged, selectedAddress]);

  // clear the session storage of any order details that were persisted previously

  // TODO: reevaluate this implementation later on
  const isCartEmptyPlus =
    isCartEmpty || (!!errorMessage && errorMessage === CONSTANTS.guidUnavailable); // if guid unavailable show empty cart view

  /**
   * Checks if a product with the given `cartEntryNumber` is being processed
   * @param cartEntryNumber {number}
   */

  useLayoutEffect(() => {
    try {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    } catch (error) {
      // just a fallback for older browsers
      window.scrollTo(0, 0);
    }
  }, [isCartEmptyPlus]);

  const isLoadingEntry = useCallback(
    // indexOf vs includes: https://jsperf.com/includes-vs-indexof/4
    (cartEntryNumber: number) => loadingEntries.indexOf(cartEntryNumber) !== -1,
    [loadingEntries]
  );

  const onSuccessCb = useCallback(
    (data: any) => {
      const { cart, entry } = data;
      const transformedCart = transfromGetCart(cart);
      dispatch(getEarnedPoints());
      updateCart(dispatch, transformedCart);
      trackCartUpdation(true, entry);
    },
    [dispatch]
  );

  const onFailureCb = useCallback(
    (productName: string, data: any) => {
      const { meta } = data;
      dispatch(
        showNotification({
          type: notificationTypes.errorCheckout,
          message: meta && meta.message ? meta.message : i18n.t('addToCartError', { productName }),
        })
      );
    },
    [dispatch]
  );

  const carousels = useRelevantCarousel({
    placements: PLACEMENTS,
    withLoader: false,
    keyword: '',
    carouselOptions: { arrows: 'compact' },
    headingStyles: { mb: 3 },
    titleStyles: {
      color: COLORS.BLACK,
      fontSize: [1, 3],
      fontWeight: 5,
    },
    productId: productIDs,
    variant: 'simple',
    isCart: true,
    onSuccessCb,
    onFailureCb,
  });

  const [isSelectItemsLink, setSelectItemsLink] = useState(() => false);
  const onItemClickHandler = (val: boolean) => {
    setSelectItemsLink(val);
  };
  return (
    <>
      <PageMaxWidth pt={[0, 5]} px={[2, 4, '', 0]}>
        <Box>
          <Breadcrumb {...breadcrumbConfig(t)} />
        </Box>
      </PageMaxWidth>

      {isCartEmptyPlus ? (
        <EmptyCartView />
      ) : (
        // @ts-ignore
        <CartCheckoutContainer>
          <CartCheckoutContainer.Flex>
            {isLoading ? (
              <CartLoader />
            ) : (
              isCartAPICompleted && (
                <CartLayout>
                  {!isSelectItemsLink &&
                    isRelevanceCarouselEnabled &&
                    productIDs &&
                    carousels[0] !== null && (
                      <RRWrapper marginBottom={24} marginTop={isMobile ? 0 : -25} isRTL={isRTL()}>
                        {carousels[0][placementArray[0]]}
                      </RRWrapper>
                    )}
                  <CartList
                    shipments={allShipments}
                    isLoadingEntry={isLoadingEntry}
                    productIDs={productIDs}
                    isSelectItemsLink={isSelectItemsLink}
                    setSelectItemsLink={onItemClickHandler}
                  />
                  <Box display={['block', 'block', 'block', 'none']}>
                    <PaymentMethod isCart />
                  </Box>
                  {!isSelectItemsLink &&
                    isRelevanceCarouselEnabled &&
                    productIDs &&
                    carousels[0] !== null && (
                      <>
                        <RRWrapper marginBottom={24} marginTop={25} isRTL={isRTL()}>
                          {carousels[0][placementArray[1]]}
                        </RRWrapper>
                        {/* <Box marginBottom={24} marginTop={25}>
                          {carousels[0][placementArray[2]]}
                        </Box> */}
                      </>
                    )}
                </CartLayout>
              )
            )}
            {!isSelectItemsLink && <CartOrderSummary isLoading={isLoading} />}
          </CartCheckoutContainer.Flex>
        </CartCheckoutContainer>
      )}
    </>
  );
};

export default memo(Cart);
